<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('serviceRequestReport.title') }}
      </h1>
      <ts-breadcrumb :items="[
        { text: $t('home'), href: '/' },
        {
          text: $t('serviceRequestReport.title'),
          active: true
        }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-md-2">
            <div class="ts-flex ts-justify-between">
              <label class="col-form-label required">{{
                $t('serviceRequestReport.dateRange')
              }}</label>
            </div>
            <a-range-picker style="width:100%;" v-model="model.date_range" format="DD-MM-YYYY" valueFormat="DD-MM-YYYY"
              :allowClear="false" />
          </div>
          <div class="col-md-2">
            <label class="col-form-label">{{
              $t('serviceRequestReport.branch')
            }}</label>
            <a-select v-model="model.branch_id" :options="branches" class="ts-w-full" :placeholder="$t('all')"
              showSearch mode="multiple" :filterOption="(input, option) =>
                option.componentOptions.children[0].text
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
                " :maxTagCount="1"></a-select>
          </div>
          <div class="col-md-2">
            <label class="col-form-label">{{
              $t('serviceRequestReport.serviceStatus')
            }}</label>
            <a-select v-model="model.service_status" :options="serviceStatus" class="ts-w-full" showSearch
              mode="multiple" :filterOption="(input, option) =>
                option.componentOptions.children[0].text
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
                " :maxTagCount="1" :placeholder="$t('all')"></a-select>
          </div>
          <div class="col-md-2">
            <label class="col-form-label">{{ $t('serviceRequestReport.serReqType') }}</label>
            <a-select v-model="model.service_request_type_id" :options="serviceRequestTypes" class="ts-w-full"
              showSearch mode="multiple" :filterOption="(input, option) =>
                option.componentOptions.children[0].text
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
                " :maxTagCount="1" :placeholder="$t('all')"></a-select>
          </div>
          <div class="col-md-2">
            <label class="col-form-label">{{ $t('serviceRequestReport.requestBy') }}</label>
            <a-select v-model="model.requested_by" :options="customers" class="ts-w-full" mode="multiple" showSearch
              @search="handleSearchCustomer" :loading="customer_searching" :placeholder="$t('all')"></a-select>
          </div>
          <div class="col-md-2">
            <label class="col-form-label">{{ $t('serviceRequestReport.informant') }}</label>
            <a-select v-model="model.informant" :options="employees" class="ts-w-full" mode="multiple"
              :placeholder="$t('all')" :filterOption="(input, option) =>
                option.componentOptions.children[0].text
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
                ">
            </a-select>
          </div>
        </div>
        <div class="row">
          <div class="col-md-2">
            <label class="col-form-label">{{ $t('serviceRequestReport.serAdmin') }}</label>
            <a-select v-model="model.owner" :options="employees" class="ts-w-full" mode="multiple"
              :placeholder="$t('all')" :filterOption="(input, option) =>
                option.componentOptions.children[0].text
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
                ">
            </a-select>
          </div>
          <div class="col-md-2">
            <label class="col-form-label">{{ $t('serviceRequestReport.technician1') }}</label>
            <a-select v-model="model.technician1" :options="employees" class="ts-w-full" mode="multiple"
              :placeholder="$t('all')" :filterOption="(input, option) =>
                option.componentOptions.children[0].text
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
                ">
            </a-select>
          </div>
          <div class="col-md-2">
            <label class="col-form-label">{{ $t('serviceRequestReport.technician2') }}</label>
            <a-select v-model="model.technician2" :options="employees" class="ts-w-full" mode="multiple"
              :placeholder="$t('all')" :filterOption="(input, option) =>
                option.componentOptions.children[0].text
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
                ">
            </a-select>
          </div>
          <div class="col-md-2">
            <label class="col-form-label">{{ $t('serviceRequestReport.plateNumber') }}</label>
            <a-select v-model="model.plate_number" :options="vehiclePlates" class="ts-w-full" mode="multiple"
              :placeholder="$t('all')" :filterOption="(input, option) =>
                option.componentOptions.children[0].text
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
                ">
            </a-select>
          </div>
          <div class="col-md-2">
            <label class="col-form-label">{{ $t('search') }}</label>
            <Input search v-model="search" :placeholder="$t('search')" />
          </div>
          <div class="col-md-2">
            <label class="col-form-label ts-w-full">{{ $t('export') }}</label>
            <ts-button class="ts-w-20 ts-h-8" outline color="success" @click.prevent="exportExcel"
              :waiting="exporting"><i class="fas fa-file-excel"></i> {{ $t('excel') }}</ts-button>
          </div>
        </div>
      </ts-card-wrapper>
      <Tabs v-model="model.report_type">
        <TabPane :label="this.$t('serviceRequestReport.summaryBySerReqType')" name="summary_service_request_type"
          icon="md-list">
          <ts-loading-banner :loading="loading">
            <SummaryServiceRequestType :summary="summary" />
          </ts-loading-banner>
        </TabPane>
        <TabPane :label="this.$t('serviceRequestReport.summaryByInformant')" name="summary_informant" icon="md-list">
          <ts-loading-banner :loading="loading">
            <SummaryInformant :summary="summary" />
          </ts-loading-banner>
        </TabPane>
        <TabPane :label="this.$t('serviceRequestReport.summaryByTechnician1')" name="summary_technician1"
          icon="md-list">
          <ts-loading-banner :loading="loading">
            <SummaryTechnician1 :summary="summary" />
          </ts-loading-banner>
        </TabPane>
        <TabPane :label="this.$t('serviceRequestReport.detailByServiceReq')" name="detail_service_request"
          icon="md-apps">
          <ts-loading-banner :loading="loading">
            <DetailServiceRequest :summary="summary" />
          </ts-loading-banner>
        </TabPane>
      </Tabs>
      <div class="d-flex justify-content-end p-15">
        <ts-i-pagination v-model="pagination" @navigate="fetchData"></ts-i-pagination>
      </div>
    </ts-card>
  </div>
</template>
<script>
import { Errors } from 'form-backend-validation'
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import SummaryServiceRequestType from './components/summary_service_request_type.vue'
import SummaryInformant from './components/summary_informant.vue'
import SummaryTechnician1 from './components/summary_technician1.vue'
import DetailServiceRequest from './components/detail_service_request.vue'
import { debounce } from 'lodash'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'
export default {
  name: 'ProductNonMovingStock',
  components: {
    SummaryServiceRequestType,
    SummaryInformant,
    SummaryTechnician1,
    DetailServiceRequest
  },
  data() {
    return {
      errors: new Errors(),
      loading: false,
      exporting: false,
      customer_searching: false,
      summary: {},
      model: {
        date_range: [
          moment()
            .startOf('month')
            .format('DD-MM-YYYY'),
          moment()
            .endOf('month')
            .format('DD-MM-YYYY')
        ],
        branch_id: [],
        service_status: [2],
        service_request_type_id: [],
        requested_by: [],
        informant: [],
        owner: [],
        technician1: [],
        technician2: [],
        plate_number: [],
        report_type: 'summary_service_request_type',
      }
    }
  },
  computed: {
    ...mapState('report/serviceOrder', ['pagination', 'formViewDatas']),
    search: {
      get() {
        return this.$store.state.report.serviceOrder.search
      },
      set(newValue) {
        this.$store.commit('report/serviceOrder/SET_SEARCH', newValue)
        this.$store.commit('report/serviceOrder/RESET_CURRENT_PAGE')
      }
    },
    branches() {
      if (this.formViewDatas['branches']) {
        return this.formViewDatas['branches'].map(el => ({
          value: el.branch_id,
          label: el.branch_name_en + ' | ' + el.branch_name_kh,
        }))
      }
      return []
    },
    serviceStatus() {
      if (this.formViewDatas['serviceStatus']) {
        return this.formViewDatas['serviceStatus'].map(el => ({
          value: el.service_status,
          label: el.label,
        }))
      }
      return []
    },
    serviceRequestTypes() {
      if (this.formViewDatas['serviceRequestTypes']) {
        return this.formViewDatas['serviceRequestTypes'].map(el => ({
          value: el.service_request_type_id,
          label: el.service_request_type,
        }))
      }
      return []
    },
    customers() {
      if (this.formViewDatas['customers']) {
        return this.formViewDatas['customers'].map(el => ({
          value: el.customer_id,
          label: el.customer_name,
        }))
      }
      return []
    },
    employees() {
      if (this.formViewDatas['employees']) {
        return this.formViewDatas['employees'].map(el => ({
          value: el.user_id,
          label: el.employee_name_kh + '|' + el.employee_name_en
        }))
      }
      return []
    },
    vehiclePlates() {
      if (this.formViewDatas['vehiclePlates']) {
        return this.formViewDatas['vehiclePlates'].map(el => ({
          value: el.plate_number,
          label: el.plate_number
        }))
      }
      return []
    },
  },
  methods: {
    ...mapActions('report/serviceOrder', ['getFormViewModel']),
    async getPreData({ params, index }) {
      this.loading = true
      await this.getFormViewModel({ index: index, params: params })
      this.loading = false
    },
    async fetchData(attributes) {
      this.loading = true
      await this.$store
        .dispatch('report/serviceOrder/serviceRequest', Object.assign(
          this.model,
          attributes
        ))
        .then((response) => {
          this.summary = response.summary
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.errors = new Errors(error.errors)
          this.notice({ type: 'error', text: error.message })
        })
    },
    exportExcel() {
      this.errors = new Errors()
      this.exporting = true

      this.$store
        .dispatch('report/serviceOrder/serviceRequestExportExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.notice({ type: 'error', text: error.message })
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    handleSearchCustomer: debounce(async function (value) {
      this.customer_searching = true
      await this.getFormViewModel({
        index: 'customers',
        params: {
          fnName: 'customers',
          search_customer: value
        }
      })
      this.customer_searching = false
    }, 800),
    notice(not) {
      this.$Notice[not.type]({
        title: 'Product Non Moving Stock',
        desc: not.text
      })
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData()
    }, 500),
    model: {
      handler(value) {
        this.fetchData()
      },
      deep: true
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.getPreData({
        params: {},
        index: undefined
      })
      vm.fetchData()
    })
  }
}
</script>
<style scoped>
.text-left {
  text-align: left;
}
</style>
